/**
 * The arithmetic behind the crop selection — WEB-09 AC3's "crops or resizes it" (`crop-dialog.tsx`).
 *
 * **It is a module of its own because it is the part that can be wrong quietly.** A rectangle
 * dragged a pixel past the edge, a handle that flips the selection inside out, a resize that
 * leaves a zero-width box: each draws plausibly and then produces a crop the server refuses or a
 * picture nobody asked for. Pulled out of the component, every rule here is a function of its
 * inputs and can be run and asserted without a browser, which is what
 * `docs/archive/plans/notes/2026-10-16-crop-geometry-checks.mjs` does.
 *
 * Everything is in the **source image's own pixels**, never in screen pixels: the dialog scales
 * the picture to its column, and a rectangle held in screen coordinates changes meaning when the
 * window is resized. `pointOf` is the one place the two coordinate systems meet.
 */

export interface IRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ISize {
  width: number;
  height: number;
}

/** Where the screen's pointer is, in the image's pixels. */
export interface IPoint {
  x: number;
  y: number;
}

/** The eight grips, named for the compass as a resize cursor is. */
export type Handle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

export const HANDLES: readonly Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

/** The smallest selection worth keeping. Below this the crop is a rounding error. */
export const MIN_SIDE = 8;

/** The frame's position on screen, as `getBoundingClientRect` gives it. */
export interface IFrame {
  left: number;
  top: number;
  width: number;
  height: number;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/**
 * A screen point in the image's pixels, clamped to the picture.
 *
 * Clamped rather than left to run negative: a drag that leaves the frame should pin the
 * selection to the edge, which is what somebody cropping to the border expects, and it keeps
 * every rectangle this module produces inside the image by construction.
 */
export function pointOf(frame: IFrame, clientX: number, clientY: number, image: ISize): IPoint {
  // A frame with no width would divide by zero — it happens for one frame while the picture is
  // still loading, and the honest answer there is the origin rather than a NaN that spreads.
  if (frame.width <= 0 || frame.height <= 0) return { x: 0, y: 0 };
  return {
    x: clamp(Math.round(((clientX - frame.left) / frame.width) * image.width), 0, image.width),
    y: clamp(Math.round(((clientY - frame.top) / frame.height) * image.height), 0, image.height)
  };
}

/** The whole picture, which is what a dialog opens on and "Whole image" returns to. */
export function whole(image: ISize): IRect {
  return { x: 0, y: 0, width: image.width, height: image.height };
}

/**
 * The rectangle between two corners of a drag, in either direction.
 *
 * Null where the drag is smaller than `MIN_SIDE` on a side — a click, or a twitch while
 * pressing — so a stray press cannot replace a selection somebody spent a minute placing.
 */
export function rectBetween(from: IPoint, to: IPoint, image: ISize): Nullable<IRect> {
  const left = clamp(Math.min(from.x, to.x), 0, image.width);
  const top = clamp(Math.min(from.y, to.y), 0, image.height);
  const right = clamp(Math.max(from.x, to.x), 0, image.width);
  const bottom = clamp(Math.max(from.y, to.y), 0, image.height);
  const rect = { x: left, y: top, width: right - left, height: bottom - top };
  return rect.width >= MIN_SIDE && rect.height >= MIN_SIDE ? rect : null;
}

/**
 * The rectangle after a handle is dragged to `point`.
 *
 * **An edge cannot cross the one opposite it.** Instead of flipping the selection — which reads
 * as the picture jumping — the dragged edge stops `MIN_SIDE` short, so the box shrinks to its
 * smallest and stays where it is. The handles that move one axis leave the other untouched.
 */
export function resized(rect: IRect, handle: Handle, point: IPoint, image: ISize): IRect {
  const right = rect.x + rect.width;
  const bottom = rect.y + rect.height;
  let { x, y } = rect;
  let width = rect.width;
  let height = rect.height;

  if (handle.includes('w')) {
    x = clamp(point.x, 0, right - MIN_SIDE);
    width = right - x;
  }
  if (handle.includes('e')) {
    width = clamp(point.x, rect.x + MIN_SIDE, image.width) - rect.x;
  }
  if (handle.includes('n')) {
    y = clamp(point.y, 0, bottom - MIN_SIDE);
    height = bottom - y;
  }
  if (handle.includes('s')) {
    height = clamp(point.y, rect.y + MIN_SIDE, image.height) - rect.y;
  }
  return { x, y, width, height };
}

/**
 * The rectangle moved by a delta, kept whole inside the picture.
 *
 * It slides along an edge rather than stopping dead at it: pushing a selection into the top of
 * the image and then sideways still moves it sideways, which is how every other drag-to-move
 * behaves.
 */
export function moved(rect: IRect, dx: number, dy: number, image: ISize): IRect {
  return {
    ...rect,
    x: clamp(rect.x + dx, 0, Math.max(0, image.width - rect.width)),
    y: clamp(rect.y + dy, 0, Math.max(0, image.height - rect.height))
  };
}

/** Whether a selection asks for anything at all — the whole picture at its own size does not. */
export function isCrop(rect: IRect, image: ISize): boolean {
  return (
    rect.x !== 0 || rect.y !== 0 || rect.width !== image.width || rect.height !== image.height
  );
}

/** A selection as percentages of the picture, which is how the overlay is drawn: the box then
 *  keeps its meaning whatever width the dialog's column happens to have. */
export function asPercentages(rect: IRect, image: ISize): Record<string, string> {
  return {
    left: `${(rect.x / image.width) * 100}%`,
    top: `${(rect.y / image.height) * 100}%`,
    width: `${(rect.width / image.width) * 100}%`,
    height: `${(rect.height / image.height) * 100}%`
  };
}
